import {
  ComponentProps,
  forwardRef,
  useEffect,
  useMemo,
  useRef,
  useState,
  ComponentType,
  useImperativeHandle,
  useCallback,
} from 'react'

import sampleSize from 'lodash/sampleSize'

import {
  faArrowsRotate,
  faCog,
  faCopy,
  faSparkles,
} from '@document-crunch/pro-regular-svg-icons'

import { useAuth } from '@dc-shared-web/providers'
import { useChat } from '@dc-shared-web/providers/ChatProvider'
import {
  ChatCitation,
  ChatCitationMap,
  ChatSettings,
  ChatTextReference,
} from '@dc-shared-web/providers/ChatProvider/types'

import {
  CrunchData,
  CrunchExtraTypeEnum,
  CrunchExtrasItem,
} from '@dc-shared-web/hooks/api/document/types'
import {
  SPEC_SOLUTION_TYPE_NAME,
  SPEC_DOC_TYPE_NAME,
  UDR_SOLUTION_TYPE_NAME,
} from '@dc-shared-web/hooks/api/document/constants'

import {
  Icon,
  IconColor,
  Tooltip,
  AIText,
} from '@dc-shared-web/components/base'
import { Button } from '@dc-shared-web/components/shadcn'
import { PageLinkComponent } from '@dc-shared-web/components/base/AIText'

import createCitationComponent from '@dc-shared-web/hoc/createCitationComponent'

import { cn } from '@dc-shared-web/lib'

import {
  CHEAT_SHEET_SUGGESTIONS,
  DEFAULT_SUGGESTIONS,
  GENERAL_DOCUMENT_SUGGESTIONS,
  DOCUMENT_TYPES_SUGGESTIONS,
  SPECIFICATIONS_REVIEW_SUGGESTIONS,
} from '@dc-shared-web/components/features/Chat/constants'
import {
  AIFeedback,
  Feedback,
  NegativeFeedbackDialog,
} from '@dc-shared-web/components/features/AIFeedback'
import {
  useCreateAIUserFeedback,
  useUpdateAIUserFeedback,
  useGetAIUserFeedback,
} from '@dc-shared-web/hooks/api/ai-user-feedback'
import { useCopyToClipboard } from '@dc-shared-web/hooks/utils/useCopyToClipboard'
import { useFSTrackEvent } from '@dc-shared-web/hooks'
import { FS } from '@dc-shared-web/lib/trackingConstants'
import { prepareFullStoryEventForCrunchData } from '@dc-shared-web/lib/utils'
import { AIAnswerFeedback } from '@dc-shared-web/hooks/api/ai-user-feedback/types'

export type ChatConversationRef = {
  scrollToBottom: () => void
  isAtBottom: () => boolean
}

export type PageLinkWithHighlightComponent = ComponentType<
  ComponentProps<PageLinkComponent> & {
    highlight?: string
  }
>

type ChatConversationProps = {
  crunchData: CrunchData
  showSuggestions: boolean
  pageLink: PageLinkWithHighlightComponent
  onOpenSettings: (settings: ChatSettings | {}) => void
  onSelectSuggestion: (text: string) => void
  onShowExplainSelection: (references: ChatTextReference[] | undefined) => void
  onShowCitationSelection?: (citation: ChatCitation | undefined) => void
  useNativeClipboard: boolean
  showMostRecentMessage?: boolean
  enablePagination?: boolean
}

const ROBOT_MESSAGE_CLASSNAME = cn(
  'tw-bg-neutral-50 tw-w-[calc(100%_-_26px)] tw-mr-[26px] tw-rounded-md tw-px-3 tw-py-1.5 tw-text-sm tw-relative',
  'after:tw-w-[26px] after:tw-left-[-26px] after:tw-h-[26px] after:tw-bg-white after:tw-rounded-br-[10px] after:tw-absolute after:tw-bottom-0 after:tw-z-0',
  'before:tw-w-[18px] before:tw-left-[-7px] before:tw-h-[26px] before:tw-bg-neutral-50 before:tw-rounded-br-[16px_14px] before:tw-absolute before:tw-bottom-0 before:tw-z-0'
)

const HUMAN_MESSAGE_CLASSNAME = cn(
  'tw-bg-blue-600 tw-text-white tw-w-[calc(100%_-_26px)] tw-ml-[26px] tw-rounded-md tw-px-3 tw-py-1.5 tw-text-sm tw-relative',
  'after:tw-w-[26px] after:tw-right-[-26px] after:tw-h-[26px] after:tw-bg-white after:tw-rounded-bl-[10px] after:tw-absolute after:tw-bottom-0 after:tw-z-0',
  'before:tw-w-[20px] before:tw-right-[-7px] before:tw-h-[26px] before:tw-bg-blue-600 before:tw-rounded-bl-[16px_14px] before:tw-absolute before:tw-bottom-0 before:tw-z-0'
)

const SPECIFICATIONS_PLACEHOLDER = (
  <>
    <p>Hello, welcome to Chat! Anything I can answer for you?</p>
    <p>
      Tip: Including specific section names or numbers in your question will
      help you get more precise answers about your specifications.
    </p>
  </>
)

const CHAT_PLACEHOLDER_OVERRIDES: Record<string, string | JSX.Element> = {
  [SPEC_SOLUTION_TYPE_NAME]: SPECIFICATIONS_PLACEHOLDER,
  [SPEC_DOC_TYPE_NAME]: SPECIFICATIONS_PLACEHOLDER,
}

const getSuggestionsByDocType = (
  crunchExtras: CrunchExtrasItem[]
): string[] => {
  const crunchExtra = crunchExtras.find(
    (crunchExtra) => crunchExtra.type === CrunchExtraTypeEnum.DocType
  )
  return (
    DOCUMENT_TYPES_SUGGESTIONS[
      crunchExtra?.value as keyof typeof DOCUMENT_TYPES_SUGGESTIONS
    ] || GENERAL_DOCUMENT_SUGGESTIONS
  )
}

const getRandomSuggestions = (crunchData: CrunchData) => {
  if (crunchData?.meta?.isCheatSheet)
    return sampleSize(CHEAT_SHEET_SUGGESTIONS, 3)
  if (crunchData?.crunchType === UDR_SOLUTION_TYPE_NAME)
    return sampleSize(
      getSuggestionsByDocType(crunchData?.meta?.crunchExtras || []),
      3
    )
  if (
    crunchData?.crunchType === SPEC_SOLUTION_TYPE_NAME ||
    crunchData?.documentTypeName === SPEC_DOC_TYPE_NAME
  )
    return sampleSize(SPECIFICATIONS_REVIEW_SUGGESTIONS, 3)
  return sampleSize(DEFAULT_SUGGESTIONS, 3)
}

const RobotMessageFooter = ({
  chatItemId,
  showDebug,
  onSettingsClick,
  crunchData,
  text,
  useNativeClipboard,
}: {
  chatItemId: string | undefined
  showDebug: boolean
  onSettingsClick: () => void
  crunchData: CrunchData | undefined
  text: string
  useNativeClipboard: boolean
}) => {
  const { copyToClipboard, buttonRef } = useCopyToClipboard({
    text,
    useNativeClipboard,
  })
  const { trackEvent } = useFSTrackEvent()
  const copiedRef = useRef<HTMLSpanElement>(null)
  const [negativeFeedbackOpen, setNegativeFeedbackOpen] = useState(false)

  const { data: feedbackData } = useGetAIUserFeedback(
    {
      content_uuid: chatItemId,
      crunch_uuid: crunchData?.id,
    },
    {
      enabled: !!crunchData?.id && !!chatItemId,
    }
  )
  const { mutate: createAIUserFeedback } = useCreateAIUserFeedback()
  const { mutate: updateAIUserFeedback } = useUpdateAIUserFeedback()
  const feedback = feedbackData?.results[0]

  const saveFeedback = useCallback(
    (feedback: AIAnswerFeedback) => {
      if (!chatItemId || !crunchData?.id) {
        return
      }

      const existingFeedback = feedbackData?.results.find(
        (f) => f.uuid === feedback.uuid
      )

      if (existingFeedback) {
        updateAIUserFeedback({
          ...existingFeedback,
          sentiment: feedback.sentiment,
          experience_feedback: feedback.experience,
          experience_feedback_other: feedback.experience_other,
          improvement_feedback: feedback.improvement,
        })
      } else {
        createAIUserFeedback({
          content_type: 'chat',
          content_uuid: chatItemId,
          crunch_uuid: crunchData?.id,
          sentiment: feedback.sentiment,
          experience_feedback: feedback.experience,
          experience_feedback_other: feedback.experience_other,
          improvement_feedback: feedback.improvement,
        })
      }
    },
    [
      chatItemId,
      crunchData?.id,
      feedbackData,
      createAIUserFeedback,
      updateAIUserFeedback,
    ]
  )

  const onSavePositiveFeedback = () => {
    saveFeedback({
      sentiment: 5,
      experience: '',
      improvement: '',
      uuid: feedback?.uuid,
    })
  }

  const onSaveNegativeFeedback = (data: Feedback) => {
    saveFeedback({
      sentiment: 0,
      experience: data.options.join(', '),
      experience_other: data.other ?? '',
      improvement: data.improvement ?? '',
      uuid: feedback?.uuid,
    })
    setNegativeFeedbackOpen(false)
  }

  const handleCopy = () => {
    if (!copiedRef.current) {
      return
    }

    copiedRef.current.style.opacity = '1'

    setTimeout(() => {
      copiedRef.current!.style.opacity = '0'
    }, 1000)

    const fullStoryEvent = prepareFullStoryEventForCrunchData(crunchData)

    trackEvent(FS.CHAT_RESPONSE_COPIED, fullStoryEvent)

    copyToClipboard?.()
  }

  const feedbackContext = {
    pageCategory: 'Crunch view',
    subpageCategory: 'Chat',
    entityId: chatItemId || '',
    entityType: 'crunch',
  }

  return (
    <div className='tw-flex tw-mt-2.5 tw-items-center'>
      <Tooltip content='Copy' withArrow>
        <Button
          variant='icon'
          className='tw-bg-transparent hover:tw-bg-primary-100'
          onClick={handleCopy}
          ref={buttonRef}
          data-clipboard-text={text}
          data-testid='copy-btn'
        >
          <Icon icon={faCopy} size='sm' className='tw-text-primary-600' />
        </Button>
      </Tooltip>

      {negativeFeedbackOpen && (
        <NegativeFeedbackDialog
          feedbackContext={feedbackContext}
          open={negativeFeedbackOpen}
          setOpen={setNegativeFeedbackOpen}
          onSubmit={onSaveNegativeFeedback}
        />
      )}

      <AIFeedback
        iconSize='sm'
        onPositiveFeedback={onSavePositiveFeedback}
        onNegativeFeedback={() => setNegativeFeedbackOpen(true)}
        feedbackContext={feedbackContext}
        value={feedback && (feedback.sentiment === 0 ? 'negative' : 'positive')}
      />

      {showDebug && (
        <Button
          variant='icon'
          className='tw-bg-transparent hover:tw-bg-primary-100'
          onClick={onSettingsClick}
          data-testid='chat-settings-btn'
        >
          <Icon
            icon={faCog}
            color={IconColor.muted}
            size='sm'
            className='tw-text-primary-600'
          />
        </Button>
      )}

      <span
        className='tw-transition-opacity tw-text-sm tw-text-neutral-300'
        style={{ opacity: 0 }}
        ref={copiedRef}
      >
        Copied!
      </span>
    </div>
  )
}

const AITextWrapper = forwardRef<HTMLDivElement, { children: React.ReactNode }>(
  ({ children }, ref) => (
    <div ref={ref} data-testid='message-text' className='tw-space-y-3'>
      {children}
    </div>
  )
)

AITextWrapper.displayName = 'AITextWrapper'

const HumanMessage = ({ text }: { text: string }) => {
  return (
    <div data-testid='message-sent'>
      <div data-testid='message-text' className={HUMAN_MESSAGE_CLASSNAME}>
        {text}
      </div>
    </div>
  )
}

const RobotMessage = ({
  chatItemId,
  text,
  showDebug,
  citations,
  crunchData,
  pageLink,
  onSettingsClick,
  useNativeClipboard,
}: {
  chatItemId: string | undefined
  text: string
  showDebug: boolean
  citations?: ChatCitationMap
  crunchData: CrunchData
  pageLink: PageLinkComponent
  onSettingsClick: () => void
  useNativeClipboard: boolean
}) => {
  const ref = useRef<HTMLDivElement>(null)

  return (
    <div data-testid='message-received' className={ROBOT_MESSAGE_CLASSNAME}>
      <AIText
        text={text}
        wrapper={({ children }) => (
          <AITextWrapper ref={ref}>{children}</AITextWrapper>
        )}
        pageLink={pageLink}
        citation={createCitationComponent(pageLink, citations)}
      />
      <RobotMessageFooter
        chatItemId={chatItemId}
        showDebug={showDebug}
        onSettingsClick={onSettingsClick}
        crunchData={crunchData}
        text={text}
        useNativeClipboard={useNativeClipboard}
      />
    </div>
  )
}

const ExplainMessage = ({
  chatItemId,
  text,
  referenceText,
  showDebug,
  crunchData,
  citations,
  pageLink,
  onSettingsClick,
  onShowExplainSelection,
  useNativeClipboard,
}: {
  chatItemId: string | undefined
  text: string
  referenceText: string
  showDebug: boolean
  crunchData: CrunchData
  citations?: ChatCitationMap
  pageLink: PageLinkComponent
  onSettingsClick: () => void
  onShowExplainSelection: () => void
  useNativeClipboard: boolean
}) => {
  const ref = useRef<HTMLDivElement>(null)

  return (
    <div data-testid='message-explainer'>
      <div className='tw-bg-neutral-50 tw-w-[calc(100%_-_26px)] tw-mr-[26px] tw-rounded-md tw-px-3 tw-py-2 tw-text-sm'>
        <div className='tw-flex tw-gap-x-2 tw-items-center tw-mb-3'>
          <Icon icon={faSparkles} color={IconColor.darkPrimary} />
          <span className='tw-text-sm tw-font-medium'>Explain</span>
        </div>
        <div className='tw-mb-1.5'>
          <span className='tw-text-sm tw-font-medium'>Source</span>
        </div>
        <div
          className='tw-bg-neutral-100 hover:tw-bg-neutral-200 tw-rounded-md hover:tw-cursor-pointer tw-p-1.5 tw-mb-3 tw-truncate tw-text-xs'
          onClick={onShowExplainSelection}
        >
          {referenceText}
        </div>
        <div className='tw-mb-1.5'>
          <span className='tw-text-sm tw-font-medium'>Answer</span>
        </div>
        <div data-testid='explainer-answer-text' className='tw-text-xs'>
          <AIText
            text={text}
            wrapper={({ children }) => (
              <AITextWrapper ref={ref}>{children}</AITextWrapper>
            )}
            pageLink={pageLink}
            citation={createCitationComponent(pageLink, citations)}
          />
        </div>
        <RobotMessageFooter
          chatItemId={chatItemId}
          showDebug={showDebug}
          onSettingsClick={onSettingsClick}
          crunchData={crunchData}
          text={text}
          useNativeClipboard={useNativeClipboard}
        />
      </div>
    </div>
  )
}

const Suggestions = ({
  crunchData,
  onSelectSuggestion,
}: {
  crunchData: CrunchData
  onSelectSuggestion: (text: string) => void
}) => {
  const [suggestions, setSuggestions] = useState<string[]>(
    getRandomSuggestions(crunchData)
  )

  const onRefresh = () => {
    setSuggestions(getRandomSuggestions(crunchData))
  }

  return (
    <div
      className={cn(
        HUMAN_MESSAGE_CLASSNAME,
        'tw-w-full tw-ml-0 tw-px-3.5 tw-py-0'
      )}
    >
      <div className='tw-flex tw-gap-x-1 tw-items-center tw-mb-1'>
        <span className='tw-text-sm tw-font-medium tw-text-white'>
          Suggestions
        </span>

        <Tooltip content='Regenerate' side='top' withArrow>
          <Button variant='icon' onClick={onRefresh} className='tw-p-0.5'>
            <Icon icon={faArrowsRotate} color={IconColor.white} />
          </Button>
        </Tooltip>
      </div>
      {suggestions.map((suggestion, index) => (
        <div
          key={index}
          className='tw-bg-white hover:tw-bg-neutral-100 tw-text-neutral-700 tw-rounded-md hover:tw-cursor-pointer tw-p-1.5 tw-mb-3 tw-text-sm'
          onClick={() => onSelectSuggestion(suggestion)}
        >
          {suggestion}
        </div>
      ))}
    </div>
  )
}

const LoadMoreButton = ({
  isLoadingMoreMessages,
  onLoadMore,
}: {
  isLoadingMoreMessages: boolean
  onLoadMore: () => void
}) => {
  return (
    <div className='tw-flex tw-justify-center tw-py-3'>
      <Button
        variant='outline'
        onClick={onLoadMore}
        disabled={isLoadingMoreMessages}
        data-testid='load-more-btn'
        className='tw-text-sm'
      >
        {isLoadingMoreMessages ? 'Loading...' : 'Load more messages'}
      </Button>
    </div>
  )
}

const Placeholder = ({ crunchData }: { crunchData: CrunchData }) => {
  return (
    <div data-testid='message-placeholder' className={ROBOT_MESSAGE_CLASSNAME}>
      {CHAT_PLACEHOLDER_OVERRIDES[crunchData.crunchType] ||
        CHAT_PLACEHOLDER_OVERRIDES[crunchData.documentTypeName] ||
        'Hello, welcome to Chat! Anything I can answer about this crunch for you?'}
    </div>
  )
}

const ChatConversation = forwardRef<ChatConversationRef, ChatConversationProps>(
  (
    {
      crunchData,
      showSuggestions,
      pageLink,
      onOpenSettings,
      onSelectSuggestion,
      onShowExplainSelection,
      useNativeClipboard,
      showMostRecentMessage = true,
      enablePagination = false,
    }: ChatConversationProps,
    ref
  ) => {
    const { user } = useAuth()
    const {
      hasLoaded,
      messages,
      citationEnabled,
      hasMoreMessages,
      loadMoreMessages,
      isLoadingMoreMessages,
    } = useChat()

    const containerRef = useRef<HTMLDivElement>(null)

    const reversedMessages = useMemo(() => [...messages].reverse(), [messages])
    const showDebug = useMemo(() => user?.is_super_admin, [user])

    useEffect(() => {
      if (showSuggestions) {
        containerRef.current?.scrollTo({
          top: containerRef.current?.scrollHeight,
          behavior: 'smooth',
        })
      }
    }, [showSuggestions])

    useImperativeHandle(ref, () => ({
      scrollToBottom: () => {
        containerRef.current?.scrollTo({
          top: containerRef.current?.scrollHeight,
          behavior: 'instant',
        })
      },
      isAtBottom: () => {
        if (!containerRef.current) return true
        const { scrollTop } = containerRef.current
        // Consider "at bottom" if within 100px of the bottom
        return Math.abs(scrollTop) <= 100
      },
    }))

    return (
      <div
        ref={containerRef}
        className='tw-grow tw-overflow-y-auto tw-overflow-x-hidden tw-flex tw-flex-col-reverse tw-p-2 tw-gap-3.5 tw-px-3 tw-py-1.5'
      >
        {hasLoaded && showSuggestions && (
          <Suggestions
            crunchData={crunchData}
            onSelectSuggestion={onSelectSuggestion}
          />
        )}
        {hasLoaded &&
          reversedMessages.map((message, index) => {
            switch (message.type) {
              case 'human':
                return (
                  <HumanMessage key={message.renderId} text={message.text} />
                )
              case 'robot':
                if (!showMostRecentMessage && index == 0) {
                  return null
                }
                return (
                  <RobotMessage
                    key={message.renderId}
                    chatItemId={message.chat_item_id}
                    text={message.text}
                    showDebug={showDebug}
                    onSettingsClick={() => onOpenSettings(message.settings)}
                    crunchData={crunchData}
                    pageLink={pageLink}
                    citations={citationEnabled ? message.citations : undefined}
                    useNativeClipboard={useNativeClipboard}
                  />
                )
              case 'explain':
                return (
                  <ExplainMessage
                    key={message.renderId}
                    chatItemId={message.chat_item_id}
                    text={message.text}
                    referenceText={message.reference_text ?? ''}
                    showDebug={showDebug}
                    onSettingsClick={() => onOpenSettings(message.settings)}
                    onShowExplainSelection={() =>
                      onShowExplainSelection(message.references)
                    }
                    crunchData={crunchData}
                    pageLink={pageLink}
                    citations={citationEnabled ? message.citations : undefined}
                    useNativeClipboard={useNativeClipboard}
                  />
                )
            }
          })}
        {hasLoaded && (!enablePagination || !hasMoreMessages) && (
          <Placeholder crunchData={crunchData} />
        )}
        {hasLoaded && hasMoreMessages && enablePagination && (
          <LoadMoreButton
            isLoadingMoreMessages={isLoadingMoreMessages}
            onLoadMore={loadMoreMessages}
          />
        )}
      </div>
    )
  }
)

ChatConversation.displayName = 'ChatConversation'

export default ChatConversation
